import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useResponsive } from '@/hooks/ui/use-responsive';
import { bgGradient } from '@/theme/css';
import LogoHorizontal from '@/components/media/logo/logo-horizontal';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@mui/material';
import { useSettingsContext } from '@/components/layout/settings';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { ASSETS_API, ENVIRONMENT } from '../../config-global';
import { EnvIcon } from '@/components/core/env-icon';
import { LanguagePopover } from '../_common';
import { RouterLink } from '@/routes/components';
import { paths } from '@/routes/paths';

type Props = {
  title?: string;
  image?: string;
  children: React.ReactNode;
};

export default function AuthClassicLayout({ children, image, title }: Props) {
  const { t } = useTranslation();
  const settings = useSettingsContext();

  const theme = useTheme();

  const mdUp = useResponsive('up', 'md');

  useEffect(() => {
    // Configurar Sentry con información básica del dispositivo
    const { screen } = window;
    Sentry.setTag('screen.resolution', `${screen.width}x${screen.height}px`);
    Sentry.setTag('screen.height', screen.height.toString());
    Sentry.setTag('screen.width', screen.width.toString());

    // Configurar Intercom solo en producción
    if (['production'].includes(ENVIRONMENT)) {
      (window as any).intercomSettings = {
        api_base: 'https://api-iam.intercom.io',
        app_id: 'q2vbp4ii',
      };

      (function () {
        const w: any = window;
        const ic = w.Intercom;
        if (typeof ic === 'function') {
          ic('reattach_activator');
          ic('update', w.intercomSettings);
        } else {
          const d = document;
          const scriptId = 'intercom-script';
          // Remove existing Intercom script if it exists
          const existingScript = d.getElementById(scriptId);
          if (existingScript) {
            existingScript.parentNode?.removeChild(existingScript);
          }

          const i: any = function () {
            i.c(arguments);
          };
          i.q = [];

          i.c = function (args: any) {
            i.q.push(args);
          };
          w.Intercom = i;

          const l = function () {
            const s = d.createElement('script');
            s.type = 'text/javascript';
            s.async = true;
            s.id = scriptId;
            s.src = 'https://widget.intercom.io/widget/q2vbp4ii';
            const x = d.getElementsByTagName('script')[0];
            x.parentNode?.insertBefore(s, x);
          };
          if (document.readyState === 'complete') {
            l();
          } else if (w.attachEvent) {
            w.attachEvent('onload', l);
          } else {
            w.addEventListener('load', l, false);
          }
        }
      })();
    }
  }, []);

  const renderLogo = (
    <Box
      sx={{
        p: { xs: 1.5, sm: 3, md: 4 },
        pt: { md: 2 },
        pb: { md: 2 },
        pl: { md: 6, lg: 8 }, // Match horizontal padding with image section
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <LogoHorizontal
        sx={{
          zIndex: 9,
          maxWidth: { xs: 150, md: 200 },
        }}
      />
    </Box>
  );

  const renderContent = (
    <Stack
      sx={{
        width: 1,
        height: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      {mdUp && (
        <Box
          sx={{
            pt: 1.5,
            pr: { xs: 2, sm: 3, md: 3 },
            display: 'flex',
            justifyContent: 'flex-end',
            flexShrink: 0,
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <LanguagePopover />
          <IconButton
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
            }}
            color="inherit"
            onClick={() =>
              settings.onUpdate('themeMode', settings.themeMode === 'dark' ? 'light' : 'dark')
            }
          >
            {settings.themeMode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
        </Box>
      )}
      <Stack
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 1,
          mx: 'auto',
          maxWidth: { xs: '100%', sm: 420, md: 480 },
          px: { xs: 2, sm: 3, md: 3 },
          py: { xs: 2, sm: 3, md: 4 },
          overflow: 'auto',
          minHeight: 0,
        }}
      >
        {children}
      </Stack>
      <Box
        component="footer"
        sx={{
          py: { xs: 2, md: 1 },
          px: 2,
          textAlign: 'center',
          flexShrink: 0,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {t('All rights reserved')} Orión {new Date().getFullYear()}
        </Typography>
        <Box sx={{ mt: 0.5 }}>
          <Link
            component={RouterLink}
            href={paths.legal.privacyPolicy}
            variant="caption"
            color="text.secondary"
            underline="hover"
          >
            {t('Privacy Policy')}
          </Link>
        </Box>
      </Box>
    </Stack>
  );

  const renderSection = (
    <Stack
      flexGrow={1}
      alignItems="center"
      justifyContent="center"
      sx={{
        px: { md: 4, lg: 6, xl: 8 },
        py: { md: 2, lg: 3 },
        position: 'relative',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Box
        component="img"
        alt="auth"
        src={image || `${ASSETS_API}/assets/login/login-red.webp`}
        sx={{
          maxWidth: {
            xs: 480,
            md: 550,
            lg: 650,
            xl: 750,
          },
          width: 'auto',
          maxHeight: '80vh',
          height: 'auto',
          objectFit: 'contain',
        }}
      />
    </Stack>
  );

  return (
    <Stack
      component="main"
      direction="row"
      sx={{
        minHeight: '100dvh',
        height: '100%',
        overflow: 'hidden',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
    >
      {mdUp && (
        <Stack
          direction="column"
          sx={{
            flex: '1 1 65%', // Explicitly set to take 65% of the space
            minHeight: '100%',
            height: '100%',
            overflow: 'auto',
            ...bgGradient({
              color: alpha(
                theme.palette.background.default,
                theme.palette.mode === 'light' ? 0.8 : 0.9
              ),
              imgUrl: `${ASSETS_API}/assets/background/overlay_2.jpg`,
            }),
          }}
        >
          <EnvIcon />
          {renderLogo}
          {renderSection}
        </Stack>
      )}

      <Stack
        sx={{
          flex: { xs: 1, md: '0 0 35%' },
          minHeight: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
          overflow: 'auto', // Allow scroll when content is too tall
          WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
        }}
      >
        {!mdUp && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: { xs: 1.5, sm: 2 },
              width: 1,
            }}
          >
            <LogoHorizontal />
            <Stack direction="row" spacing={0.5}>
              <LanguagePopover />
              <IconButton
                color="inherit"
                onClick={() =>
                  settings.onUpdate('themeMode', settings.themeMode === 'dark' ? 'light' : 'dark')
                }
              >
                {settings.themeMode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
              </IconButton>
            </Stack>
          </Box>
        )}
        <EnvIcon />
        {renderContent}
      </Stack>
    </Stack>
  );
}
